import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Package,
  AlertTriangle,
  XCircle,
  CheckCircle,
  HelpCircle,
  ExternalLink,
  Info,
  Loader2,
  ChevronDown,
  ChevronRight,
  Plus,
  FlaskConical,
  Table,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import { useProductScanner } from '../hooks/useProductScanner';
import type { ProductLookupResponse, IngredientFlag, NutritionFacts, ProductType } from '../types';
import { RISK_LEVELS } from '../types';
import { ProductSuggestionModal } from '../components/ProductSuggestionModal';
import { IngredientReportModal } from '../components/IngredientReportModal';

export function ProductDetailPage() {
  const { t } = useTranslation('scanner');
  const { barcode } = useParams<{ barcode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { lookupProduct, isLoading, error } = useProductScanner();

  const [productData, setProductData] = useState<ProductLookupResponse | null>(
    location.state?.productData || null
  );
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [showNutrition, setShowNutrition] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [showIngredientReportModal, setShowIngredientReportModal] = useState(false);

  // Get product type from state or default to food
  const productType: ProductType = location.state?.productType || productData?.productType || 'food';

  // Load product if not passed via state (don't save to history again)
  useEffect(() => {
    if (!productData && barcode) {
      lookupProduct(barcode, productType, false).then((result) => {
        if (result) {
          setProductData(result);
        }
      });
    }
  }, [barcode, productData, lookupProduct, productType]);

  // Loading state
  if (isLoading && !productData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-pp-orange animate-spin mx-auto mb-4" />
          <p className="text-white/70">{t('productDetail.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || (!isLoading && !productData)) {
    return (
      <>
        <h1 className="text-white text-xl font-semibold mb-6">{t('productDetail.title')}</h1>
        <div className="glass-effect rounded-[15px] p-8 text-center">
          <HelpCircle className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">
            {t('productDetail.notFound')}
          </h2>
          <p className="text-white/70 mb-6">
            {error || t('productDetail.notFoundDescription', { barcode })}
          </p>
          <button
            onClick={() => navigate('/tools/scanner')}
            className="px-6 py-2.5 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90"
          >
            {t('productDetail.back')}
          </button>
        </div>
      </>
    );
  }

  const { product, safety, found } = productData!;

  // Product not found in database
  if (!found || !product) {
    return (
      <>
        <h1 className="text-white text-xl font-semibold mb-6">{t('productDetail.title')}</h1>
        <div className="glass-effect rounded-[15px] p-8 text-center">
          <HelpCircle className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">
            {t('productDetail.notInDatabase')}
          </h2>
          <p className="text-white/50 text-sm mb-2">Barcode: {barcode}</p>
          <p className="text-white/70 mb-6">
            {t('productDetail.notInDatabaseDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/tools/scanner')}
              className="px-6 py-2.5 bg-white/20 border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/30"
            >
              {t('productDetail.back')}
            </button>
            <button
              onClick={() => setShowSuggestionModal(true)}
              className="px-6 py-2.5 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('productDetail.suggestProduct')}
            </button>
          </div>
        </div>

        <ProductSuggestionModal
          isOpen={showSuggestionModal}
          onClose={() => setShowSuggestionModal(false)}
          barcode={barcode}
          productType={productType}
        />
      </>
    );
  }

  const riskConfig = RISK_LEVELS[safety.overall_risk];

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-4">{t('productDetail.title')}</h1>

      {/* Risk Status Badge */}
      <div className="flex justify-center mb-4">
        <div className={cn(
          'rounded-[15px] px-4 py-2 flex items-center gap-2 font-semibold',
          safety.overall_risk === 'safe' && 'bg-pp-teal text-white',
          safety.overall_risk === 'caution' && 'bg-pp-orange text-white',
          safety.overall_risk === 'avoid' && 'bg-red-500/90 text-white',
          safety.overall_risk === 'unknown' && 'bg-gray-500/80 text-white',
        )}>
          {safety.overall_risk === 'safe' && <CheckCircle className="w-5 h-5" />}
          {safety.overall_risk === 'caution' && <AlertTriangle className="w-5 h-5" />}
          {safety.overall_risk === 'avoid' && <XCircle className="w-5 h-5" />}
          {safety.overall_risk === 'unknown' && <HelpCircle className="w-5 h-5" />}
          <span>{riskConfig.labelDe}</span>
        </div>
      </div>

      {/* Product Info */}
      <div className="mb-4">
        <div className="flex gap-4">
          {/* Product Image */}
          <div className="w-[70px] h-[70px] bg-white/10 rounded-[10px] flex items-center justify-center overflow-hidden flex-shrink-0">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <Package className="w-8 h-8 text-white/40" />
            )}
          </div>

          {/* Product Details */}
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-white mb-1">{product.name}</h2>
            {product.brand && (
              <p className="text-sm text-white/70 mb-0.5">{product.brand}</p>
            )}
            {product.quantity && (
              <p className="text-sm text-white/70">{product.quantity}</p>
            )}

            {/* Nutriscore */}
            {product.nutriscore_grade && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-white/70">{t('productDetail.nutriScore')}</span>
                <span
                  className={cn(
                    'text-xs font-bold px-2 py-0.5 rounded uppercase',
                    {
                      'bg-green-500 text-white': product.nutriscore_grade === 'a',
                      'bg-lime-500 text-white': product.nutriscore_grade === 'b',
                      'bg-yellow-500 text-white': product.nutriscore_grade === 'c',
                      'bg-orange-500 text-white': product.nutriscore_grade === 'd',
                      'bg-red-500 text-white': product.nutriscore_grade === 'e',
                    }
                  )}
                >
                  {product.nutriscore_grade}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Accordions */}
      <div className="space-y-3 mb-4">
        {/* All Ingredients */}
        {product.ingredients_text && (
          <div className="glass-effect rounded-[15px] overflow-hidden">
            <button
              onClick={() => setShowAllIngredients(!showAllIngredients)}
              className="w-full px-4 py-4 flex items-center gap-3 hover:bg-white/5 transition-colors"
            >
              <Info className="w-5 h-5 text-white/70" />
              <span className="flex-1 text-left font-medium text-white">{t('productDetail.allIngredients')}</span>
              {showAllIngredients ? (
                <ChevronDown className="w-5 h-5 text-white/60" />
              ) : (
                <ChevronRight className="w-5 h-5 text-pp-orange" />
              )}
            </button>
            {showAllIngredients && (
              <div className="px-4 pb-4 border-t border-white/10 pt-3">
                <p className="text-sm text-white/80 leading-relaxed">
                  {product.ingredients_text}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Nutrition Facts */}
        {product.nutriments && (
          <div className="glass-effect rounded-[15px] overflow-hidden">
            <button
              onClick={() => setShowNutrition(!showNutrition)}
              className="w-full px-4 py-4 flex items-center gap-3 hover:bg-white/5 transition-colors"
            >
              <Table className="w-5 h-5 text-white/70" />
              <span className="flex-1 text-left font-medium text-white">{t('productDetail.nutritionTitle')}</span>
              {showNutrition ? (
                <ChevronDown className="w-5 h-5 text-white/60" />
              ) : (
                <ChevronRight className="w-5 h-5 text-pp-orange" />
              )}
            </button>
            {showNutrition && (
              <div className="px-4 pb-4 border-t border-white/10">
                <NutritionTable nutriments={product.nutriments} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Source Button */}
      <div className="flex justify-end mb-6">
        <a
          href={`https://world.openfoodfacts.org/product/${barcode}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-4 py-2 bg-pp-orange rounded-[5px] text-white text-sm font-medium hover:bg-pp-orange/90"
        >
          <ExternalLink className="w-4 h-4" />
          {t('productDetail.source')}
        </a>
      </div>

      {/* Flagged Ingredients */}
      {safety.flagged_ingredients.length > 0 && (
        <section className="mb-6">
          <h3 className="text-white font-semibold mb-3">
            {t('productDetail.criticalIngredients')}
          </h3>
          <div className="space-y-3">
            {safety.flagged_ingredients.map((ingredient, index) => (
              <IngredientItem key={index} ingredient={ingredient} />
            ))}
          </div>
        </section>
      )}

      {/* Report Issue */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-white">
              {t('productDetail.reportError')}
            </h3>
            <p className="text-sm text-white/60">
              {t('productDetail.reportErrorDescription')}
            </p>
          </div>
          <button
            onClick={() => setShowIngredientReportModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-pp-orange rounded-[5px] text-white text-sm font-medium hover:bg-pp-orange/90"
          >
            <FlaskConical className="w-4 h-4" />
            {t('productDetail.report')}
          </button>
        </div>
      </div>

      {/* Modals */}
      <ProductSuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => setShowSuggestionModal(false)}
        barcode={barcode}
        productType={productType}
      />

      <IngredientReportModal
        isOpen={showIngredientReportModal}
        onClose={() => setShowIngredientReportModal(false)}
        productType={productType}
        productName={product.name}
      />
    </>
  );
}

// Ingredient Item Component
function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function IngredientItem({ ingredient }: { ingredient: IngredientFlag }) {
  const isAvoid = ingredient.risk_level === 'avoid';
  const hintColor = isAvoid ? 'text-red-400' : 'text-pp-orange';

  return (
    <div className="bg-white/10 rounded-[15px] p-4">
      <div className="flex items-center gap-2 mb-2">
        {isAvoid ? (
          <XCircle className={cn('w-4 h-4 flex-shrink-0', hintColor)} />
        ) : (
          <AlertTriangle className={cn('w-4 h-4 flex-shrink-0', hintColor)} />
        )}
        <h4 className={cn('font-medium text-sm', hintColor)}>
          {capitalizeFirst(ingredient.name)}
        </h4>
      </div>
      <p className="text-sm text-white">{ingredient.reason}</p>
    </div>
  );
}

// Nutrition Facts Table Component
function NutritionTable({ nutriments }: { nutriments: NutritionFacts }) {
  const { t } = useTranslation('scanner');
  const rows: { label: string; value: string; indent?: boolean }[] = [];

  if (nutriments.energy_kcal !== undefined) {
    rows.push({ label: t('productDetail.energy'), value: `${Math.round(nutriments.energy_kcal)} kcal` });
  }
  if (nutriments.fat !== undefined) {
    rows.push({ label: t('productDetail.fat'), value: `${nutriments.fat.toFixed(1)} g` });
  }
  if (nutriments.saturated_fat !== undefined) {
    rows.push({ label: t('productDetail.saturatedFat'), value: `${nutriments.saturated_fat.toFixed(1)} g`, indent: true });
  }
  if (nutriments.carbohydrates !== undefined) {
    rows.push({ label: t('productDetail.carbohydrates'), value: `${nutriments.carbohydrates.toFixed(1)} g` });
  }
  if (nutriments.sugars !== undefined) {
    rows.push({ label: t('productDetail.sugar'), value: `${nutriments.sugars.toFixed(1)} g`, indent: true });
  }
  if (nutriments.fiber !== undefined) {
    rows.push({ label: t('productDetail.fiber'), value: `${nutriments.fiber.toFixed(1)} g` });
  }
  if (nutriments.proteins !== undefined) {
    rows.push({ label: t('productDetail.protein'), value: `${nutriments.proteins.toFixed(1)} g` });
  }
  if (nutriments.salt !== undefined) {
    rows.push({ label: t('productDetail.salt'), value: `${nutriments.salt.toFixed(2)} g` });
  }
  if (nutriments.alcohol !== undefined && nutriments.alcohol > 0) {
    rows.push({ label: t('productDetail.alcohol'), value: `${nutriments.alcohol.toFixed(1)} % vol` });
  }

  if (rows.length === 0) {
    return <p className="mt-3 text-sm text-white/50">{t('productDetail.noNutrition')}</p>;
  }

  return (
    <div className="mt-3">
      {rows.map((row, index) => (
        <div
          key={index}
          className={cn(
            'flex justify-between py-2 text-sm',
            index < rows.length - 1 && 'border-b border-white/10'
          )}
        >
          <span className={cn(
            'text-white/70',
            row.indent && 'pl-4 text-white/50'
          )}>
            {row.label}
          </span>
          <span className={cn(
            'font-medium text-white',
            row.label === t('productDetail.alcohol') && 'text-red-400'
          )}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}
